/**
 * The request body for creating or editing a scheduled organizer message.
 *
 * Extracted from TripCalendarView.vue so the one rule that is easy to get
 * wrong can be unit tested. It WAS wrong: the timezone key was omitted
 * whenever the manager had not picked a zone, for both create and edit, and
 * the two backend handlers do not read an absent key the same way.
 *
 *   POST /api/rides/<id>/organizer_messages
 *     always reads body['timezone'], so ABSENT means NULL, i.e. "resolve
 *     this day's local zone". Omitting the key is correct here.
 *
 *   PUT /api/organizer_messages/<id>
 *     is PATCH-shaped: `if 'timezone' in body` -- so ABSENT means LEAVE
 *     UNCHANGED, exactly like its body/target_date/send_time handling.
 *
 * The consequence of treating those the same: a manager who picked
 * "Italy - Rome", then edited the message back to "this day's local time",
 * sent no timezone key, the field kept its old value, and the message went
 * out at the Rome hour. The modal closed successfully and the calendar never
 * displays a message's zone, so nothing told them.
 *
 * So on edit the key is ALWAYS present, and an empty string is the explicit
 * "clear it" value -- which the backend's _parse_timezone already treats as
 * None (travelerblog/whatsapp/api.py). "Set it back to the default" is a
 * value, not an absence.
 */

const ORGANIZER_PAYLOAD_CREATE = 'add';

function buildOrganizerMessagePayload( form, mode ) {
  const zone = form.timezone || '';

  if ( mode === ORGANIZER_PAYLOAD_CREATE ) {
    const payload = {
      body: form.body,
      send_time: form.sendTime,
      // WHO it is for, and only on CREATE. The server fixes the recipient at
      // creation (400 recipient_type_immutable on a change) because
      // travellers may already hold a button for the message, or the manager
      // may already have been notified -- neither direction has an answer.
      // Sending it on edit would make a correct PUT depend on the form still
      // holding the stored value, which is exactly the class of bug the
      // timezone rule below exists for.
      recipient_type: form.recipientType || 'registered_users',
      target_date: form.dayDate
    };
    // Absent == NULL == this day's local zone, on the create endpoint only.
    if ( zone ) {
      payload.timezone = zone;
    }
    return payload;
  }

  const payload = {
    body: form.body,
    send_time: form.sendTime,
    timezone: zone
  };

  // target_date is sent on EDIT only when the manager actually changed it.
  //
  // The date is editable in the modal now, so it has to be sendable -- but
  // sending it on every edit is what made this sequence a dead end: write a
  // message for 5 Oct inside a 3-11 Oct trip, later move the trip to 20-25
  // Oct (the exact case the window's second enforcement point exists for),
  // then open the message to fix a typo. The server re-ran the window check
  // on the unchanged date and returned 400 date_outside_window about a date
  // the manager had not touched. The only escape was delete-and-recreate,
  // which bills a second template send to everyone who got the first.
  //
  // Comparing against the value the modal opened with is what separates
  // "the manager moved this message" from "the manager fixed a typo". The
  // PUT endpoint is PATCH-shaped (`if 'target_date' in body_json`), so
  // omitting the key means "leave it alone", and an unchanged date is never
  // re-validated.
  if ( form.dayDate && form.dayDate !== form.originalDayDate ) {
    payload.target_date = form.dayDate;
  }
  return payload;
}

// CommonJS, matching src/utils/tripCalendarFiltering.js: webpack consumes it
// from the component, and tests/unit can require it under plain node without
// a transpile step.
module.exports = { buildOrganizerMessagePayload, ORGANIZER_PAYLOAD_CREATE };
